import { useMemo, useState } from 'react'
import { Dialog } from '@/shared/components/ui/Dialog'
import { useI18n } from '@/shared/i18n/useT'
import type { GridSignRule } from './signRule'

type Choice = 'positive' | 'negative' | 'keep'

interface Props {
  /** Header of the column the rule is for. */
  readonly columnName: string
  /** Columns the sign can follow: every other column of the grid. */
  readonly conditionColumns: readonly { field: string; name: string }[]
  /** field → values to offer: the column's options and what its cells hold. */
  readonly valuesOf: (field: string) => readonly string[]
  readonly rule: GridSignRule | undefined
  /** How many existing rows a rule would rewrite, for the confirmation line. */
  readonly affectedRows: (rule: GridSignRule | null) => number
  readonly busy: boolean
  readonly onSave: (rule: GridSignRule | null) => void
  readonly onClose: () => void
  /** Replaces the sign wording, for another rule of the same shape (the exchange-rate quote rule). */
  readonly labels?: Partial<Record<'title' | 'description' | 'follows' | 'positive' | 'negative' | 'keep' | 'remove', string>>
}

/**
 * Edits a number column's sign rule (see `signRule.ts`): which column decides
 * the sign, and for each of its values whether this column turns positive,
 * negative, or keeps what was typed. Existing rows are rewritten on save; the
 * footer says how many first.
 */
export function SignRuleDialog({ columnName, conditionColumns, valuesOf, rule, affectedRows, busy, onSave, onClose, labels }: Props) {
  const { t } = useI18n()
  const [field, setField] = useState(rule?.field ?? conditionColumns[0]?.field ?? '')
  const [choices, setChoices] = useState<Record<string, Choice>>(() => {
    const initial: Record<string, Choice> = {}
    for (const value of rule?.positive ?? []) initial[value] = 'positive'
    for (const value of rule?.negative ?? []) initial[value] = 'negative'
    return initial
  })

  const values = useMemo(() => {
    const seen = new Map<string, string>()
    const add = (value: string) => {
      const key = value.trim().toLowerCase()
      if (key !== '' && !seen.has(key)) seen.set(key, value.trim())
    }
    valuesOf(field).forEach(add)
    if (rule?.field === field) [...rule.positive, ...rule.negative].forEach(add)
    return [...seen.values()]
  }, [field, rule, valuesOf])

  const draft: GridSignRule | null = useMemo(() => {
    const positive = values.filter((v) => choices[v] === 'positive')
    const negative = values.filter((v) => choices[v] === 'negative')
    return field && (positive.length > 0 || negative.length > 0) ? { field, positive, negative } : null
  }, [choices, field, values])

  const affected = affectedRows(draft)
  const choiceLabels: Record<Choice, string> = {
    positive: labels?.positive ?? t('grid.signRule.positive'),
    negative: labels?.negative ?? t('grid.signRule.negative'),
    keep: labels?.keep ?? t('grid.signRule.keep'),
  }

  return (
    <Dialog
      title={labels?.title ?? t('grid.signRule.title', { column: columnName })}
      description={labels?.description ?? t('grid.signRule.description')}
      onClose={onClose}
      footer={
        <>
          {affected > 0 && (
            <p role="status" className="mr-auto self-center text-xs text-text-muted">
              {t('grid.signRule.affected', { count: affected })}
            </p>
          )}
          {rule && (
            <button type="button" disabled={busy} onClick={() => onSave(null)} className="tap-target rounded-full border border-border px-4 text-sm text-text hover:bg-surface-3 disabled:opacity-50">
              {labels?.remove ?? t('grid.signRule.remove')}
            </button>
          )}
          <button type="button" onClick={onClose} className="tap-target rounded-full border border-border px-4 text-sm text-text hover:bg-surface-3">
            {t('common.cancel')}
          </button>
          <button type="button" disabled={busy || !draft} onClick={() => onSave(draft)} className="tap-target rounded-full bg-brand px-4 text-sm text-brand-fg hover:opacity-90 font-medium disabled:opacity-50">
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{labels?.follows ?? t('grid.signRule.follows')}</span>
          <select
            value={field}
            onChange={(event) => { setField(event.target.value); setChoices({}) }}
            className="nt-control"
          >
            {conditionColumns.map((c) => <option key={c.field} value={c.field}>{c.name}</option>)}
          </select>
        </label>

        {values.length === 0 ? (
          <p className="text-sm text-text-muted">{t('grid.signRule.noValues')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-text-muted">
                <th className="py-1 font-medium">{t('grid.signRule.whenValue')}</th>
                {(['positive', 'negative', 'keep'] as const).map((choice) => (
                  <th key={choice} className="py-1 text-center font-medium">{choiceLabels[choice]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {values.map((value) => (
                <tr key={value} className="border-t border-border">
                  <td className="py-1.5 pr-2">{value}</td>
                  {(['positive', 'negative', 'keep'] as const).map((choice) => (
                    <td key={choice} className="py-1.5 text-center">
                      <input
                        type="radio"
                        name={`sign-${value}`}
                        aria-label={`${value}: ${choiceLabels[choice]}`}
                        checked={(choices[value] ?? 'keep') === choice}
                        onChange={() => setChoices((current) => ({ ...current, [value]: choice }))}
                        className="h-4 w-4 accent-brand"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Dialog>
  )
}
